// ============================================
// Image Loader — loads images from a single .tar archive
// Replaces individual file loading for itch.io builds (1000 file limit)
//
// Two modes:
// - Desktop: extract all Blob URLs upfront (fast rendering, ~200MB RAM)
// - Mobile: keep tar as ArrayBuffer, create Blob URLs on-demand with LRU cache
// ============================================

import { fetchWithEdgeRetry } from './engine/story-fetch.js';

const _imageBlobs = {};    // name → blob URL (eager mode + lazy cache)
const _allBlobUrls = new Set();  // every blob URL ever created — for pagehide revoke
let _tarLoaded = false;
let _tarLoading = null;
let _tarAvailable = false;

// Lazy mode state
let _lazyMode = false;
// Full archive as a Blob: disk-backable (the browser may page it out, unlike
// a JS-heap ArrayBuffer that pinned ~the full tar in memory all session) and
// _extractLazy slices are zero-copy views into it.
let _tarBlob = null;
let _tarIndex = null;      // name → { offset, size, mime } (lazy mode index)
const _LRU_MAX_MOBILE = 60;   // max cached blob URLs on mobile
const _LRU_MAX_DESKTOP = 200; // larger cache on desktop (more RAM)
let _LRU_MAX = 60;            // set during init
const _lruQueue = [];      // LRU eviction queue (oldest first)

// Split-tar (0.2.8.1.2): the single images.tar outgrew reliable mobile-web
// download (~170MB → Android sessions started image-less, VPS 0.2.8.1.1). It's
// now split into a small CORE tar (images.tar — UI / Portraits / Apartment /
// Locations / Items / SMS / System; the game waits on it) and a larger REST
// tar (images-rest.tar — heavy per-location/event art) that streams in the
// BACKGROUND, best-effort: a rest failure leaves the heavy art missing but the
// game playable on the core set instead of a blank session. Both are indexed
// by basename (unique post-dedup); getImageUrl/_extractLazy check core first,
// then rest. Older builds without a rest tar just 404 the rest fetch (no-op).
let _restBlob = null;
let _restIndex = null;
let _restLoading = null;
let _restLoaded = false;
let _restRetryTimer = null;       // background self-heal timer (mirrors _tarRetryTimer)
let _restRetryCount = 0;          // bounded retry budget, reuses _TAR_RETRY_DELAYS
let _restLoadFailed = false;      // breadcrumb: rest tar exhausted retries this session
const REST_TAR_URL = 'assets/images-rest.tar';

// Evicted blob URLs that were still referenced by live DOM nodes at
// eviction time — re-checked and revoked on the next sweep (panel clear).
const _pendingRevoke = new Set();

// Blob URLs woven into CSSOM rules (main.js _interceptCssRuleUrls rewrites
// stylesheet rules like the narrator ornament) are INVISIBLE to the
// [src]/[style*] reference check — and main.js skips rules that already
// contain `blob:`, so a revoked one would never re-resolve. Pin them for
// the document lifetime; pagehide still revokes via _allBlobUrls.
const _pinnedUrls = new Set();

/** Exempt a blob URL from eviction revoke (CSSOM-rule references). */
export function pinBlobUrl(url) {
    if (url) _pinnedUrls.add(url);
}

// Tar integrity / recovery state. _tarComplete means the indexed buffer
// covers the full archive (received === content-length). A truncated or
// failed download schedules background re-download attempts so the
// session self-heals instead of running image-less until restart
// (VPS reports 2026-06-09 "No pic loading" / "Images stopped").
let _tarComplete = false;
let _tarRetryCount = 0;
let _tarRetryTimer = null;
const _TAR_RETRY_DELAYS = [15000, 45000, 120000];

// Dead-backing recovery (foundby VPS Android 0.3.0.5): a fully-loaded tar Blob
// can lose its disk backing mid-session (browser pages the ~62MB core / 147MB
// rest Blob to disk, then the OPFS/cache backing is evicted or the itch iframe
// storage partition is torn down). Every subsequent slice().arrayBuffer()
// throws NotReadableError ("...could not be read...permission problems...after a
// reference to a file was acquired"). The blob is `_tarComplete === true`, so
// NONE of the existing heals fire (all gated on !_tarComplete) and even the
// manual retry button no-ops. A re-fetch into a FRESH in-memory Blob recovers
// it — the dead backing was the OLD blob's disk spill. `_tarDeadHealing`
// debounces the per-image storm (15+ images fail at once) to ONE re-download.
let _tarDeadHealing = false;

/** True when an error looks like a lost Blob disk backing (re-fetchable). */
function _isDeadBackingError(e) {
    if (!e) return false;
    if (e.name === 'NotReadableError' || e.name === 'NotFoundError') return true;
    return /could not be read|not be read|NotReadable|permission problems/i.test(e.message || '');
}

/**
 * The core tar Blob's backing died (NotReadableError on a complete tar). Clear
 * the completion flags + drop the dead Blob so the heal path's `_tarComplete`
 * guard passes, then re-download into a fresh in-memory Blob. Owner choice
 * 2026-07-16: CORE (62MB) auto-heals hands-free; REST (147MB) is manual.
 * Debounced via `_tarDeadHealing` so a per-image failure storm kicks ONE fetch.
 */
function _recoverDeadCoreTar(reason) {
    if (_tarDeadHealing) return;
    _tarDeadHealing = true;
    console.warn(`[ImageLoader] core tar backing unreadable (${reason}) — forcing fresh re-download`);
    _tarComplete = false;
    _tarLoaded = false;
    _tarBlob = null;
    _tarIndex = null;
    _tarRetryCount = 0;
    if (_tarRetryTimer) { clearTimeout(_tarRetryTimer); _tarRetryTimer = null; }
    _dataUriCache.clear();   // cached data: URIs point at the dead blob's bytes
    _emitTarState(false);    // surface the "images loading" banner while it re-fetches
    Promise.resolve(_runTarHeal('Dead-backing core re-download')).finally(() => {
        _tarDeadHealing = false;
    });
}

/**
 * The rest tar Blob's backing died. Owner choice: do NOT auto-refetch the 147MB
 * rest tar on mobile data — clear its flags so the on-demand rest-heal and the
 * manual retry banner CAN re-fetch it, and surface the banner. `retryTarNow`
 * (banner tap) and the demand-triggered rest heal then reload it.
 */
function _markRestBackingDead(reason) {
    if (!_restLoaded && !_restBlob) return;
    console.warn(`[ImageLoader] rest tar backing unreadable (${reason}) — cleared for manual/on-demand re-fetch`);
    _restLoaded = false;
    _restLoadFailed = true;
    _restBlob = null;
    _restIndex = null;
    if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
    _emitTarState(false);
}

/**
 * Detect mobile: touch + small screen or mobile UA.
 */
function _isMobile() {
    if (typeof window === 'undefined') return false;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const smallScreen = window.innerWidth <= 1024;
    const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    return (hasTouch && smallScreen) || mobileUA;
}

/**
 * Probe if tar bundle exists (called during init).
 * Uses HEAD to avoid opening a body-stream connection that some
 * servers (and Firefox's HTTP/1.0 reuse path) won't clean up cleanly
 * after AbortController.abort() — that previously stalled Firefox
 * at 8% "Checking image archive..." forever (2026-04-17).
 */
export async function probeImageTar() {
    // The HEAD probe decides tar-vs-loose for the WHOLE session — a single
    // transient BunnyCDN 403 here used to doom the session to imageless
    // loose mode with zero recovery (scene images don't ship loose on the
    // Free build). Retry with cache:'reload' (don't replay a negatively
    // cached edge response), then fall back to the Cache API copy from a
    // previous session — which is also the only image source when offline.
    for (let attempt = 1; attempt <= 3; attempt++) {
        try {
            const resp = await fetch('assets/images.tar', {
                method: 'HEAD',
                cache: attempt === 1 ? 'default' : 'reload',
            });
            if (resp.ok) {
                _tarAvailable = true;
                console.log('[ImageLoader] TAR bundle detected');
                return true;
            }
            console.warn(`[ImageLoader] tar HEAD probe ${attempt}/3: HTTP ${resp.status}`);
        } catch(e) {
            console.warn(`[ImageLoader] tar HEAD probe ${attempt}/3 failed: ${e?.message || e}`);
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 600));
    }
    try {
        if (await _tryLoadFromCache(0)) {
            _tarAvailable = true;
            console.warn('[ImageLoader] tar probe failed — using Cache API copy from a previous session');
            return true;
        }
    } catch(e) { /* fall through to loose mode */ }
    _tarAvailable = false;
    return false;
}

/**
 * Is the tar bundle available?
 */
export function isTarMode() {
    return _tarAvailable;
}

/**
 * Load and parse the tar archive.
 * Mobile: builds index only, keeps ArrayBuffer for on-demand extraction.
 * Desktop: extracts all Blob URLs upfront.
 */
// ── Tar Cache (Cache API) ─────────────────────────────────────────
// Pre-v0.2.7 cached the whole 140MB tar as a single IndexedDB value via
// localforage. Worked on desktop, but Chrome Android silently rejects single
// IDB values above ~64MB ("Failed to read large IndexedDB value" is Chrome's
// own error, not ours) — every mobile session re-downloaded the full tar
// over cellular. Cache API is designed for large HTTP-response bodies and
// has no per-value cap; we treat the local fetch result as the cache entry.
// Foundby:VPS player v0.2.6.7 Android Chrome.
const TAR_CACHE_NAME = 'PnO_TarCache_v1';
const TAR_CACHE_URL = 'assets/images.tar';

function _hasCacheAPI() {
    // file:// (Electron desktop build, or an extracted web zip opened locally)
    // exposes the Cache API, but cache.put() rejects any request whose scheme
    // resolves to file: ("Request scheme 'file' is unsupported"). TAR_CACHE_URL
    // is relative, so on file:// it resolves against the file:// base and every
    // write throws — the persistent-cache copy can NEVER be created, so the read
    // can never hit either. Skipping the API on file:// removes the doomed
    // cache.put() attempt and its non-fatal warning (VPS reports 0.2.8.1,
    // "Cache write failed ... scheme 'file' is unsupported"). No behaviour
    // change for image availability: file:// sessions already re-load the tar
    // from local disk into memory every launch (the Cache API never persisted
    // anything there), and that path is unchanged.
    try {
        if (typeof location !== 'undefined' && location && location.protocol === 'file:') return false;
    } catch (e) { /* no location (worker?) — fall through to capability check */ }
    return typeof caches !== 'undefined' && caches && typeof caches.open === 'function';
}

/**
 * Try to load tar from Cache API + rebuild index from the buffer.
 * Returns true if cache hit (size matches), false otherwise.
 */
async function _tryLoadFromCache(expectedSize) {
    if (!_hasCacheAPI()) return false;
    try {
        const cache = await caches.open(TAR_CACHE_NAME);
        const cached = await cache.match(TAR_CACHE_URL);
        if (!cached) return false;

        const cachedSize = parseInt(cached.headers.get('content-length') || '0', 10);
        if (expectedSize && cachedSize && cachedSize !== expectedSize) {
            console.log(`[ImageLoader] Cache stale (${cachedSize} vs ${expectedSize})`);
            await cache.delete(TAR_CACHE_URL);
            return false;
        }

        // Idempotent here so EVERY entry point is safe — the probe-failure
        // fallback reaches this without going through _doLoadTar, and
        // without _lazyMode getImageUrl would never extract a single blob.
        _lazyMode = true;
        _LRU_MAX = _isMobile() ? _LRU_MAX_MOBILE : _LRU_MAX_DESKTOP;

        const blob = await cached.blob();
        // Transient byte view ONLY for index parsing — dropped right after,
        // the session keeps the disk-backable Blob.
        const buf = new Uint8Array(await blob.arrayBuffer());
        // Build into a local index, swap blob+index together — never leave
        // a window where lookups run against a half-built index.
        const index = await _buildIndex(buf);
        _tarBlob = blob;
        _tarIndex = index;
        _tarLoaded = true;
        // Only complete archives are ever written to the cache (truncated
        // downloads skip _saveTarToCache), so a size-validated hit is full.
        _tarComplete = true;
        console.log(`[ImageLoader] Loaded from cache (${Object.keys(_tarIndex).length} images, ${(_tarBlob.size / 1048576).toFixed(1)}MB)`);
        _emitTarState(true);
        return true;
    } catch(e) {
        console.warn('[ImageLoader] Cache read failed:', e.message);
        return false;
    }
}

/**
 * Save tar to Cache API. The buffer is wrapped in a Response so the next
 * load can stream it back via cache.match().
 */
async function _saveTarToCache(buffer, tarSize) {
    if (!_hasCacheAPI()) return;
    try {
        const cache = await caches.open(TAR_CACHE_NAME);
        const response = new Response(buffer, {
            headers: {
                'Content-Type': 'application/x-tar',
                'Content-Length': String(tarSize),
            },
        });
        await cache.put(TAR_CACHE_URL, response);
        console.log(`[ImageLoader] Cached tar to Cache API (${(buffer.length / 1048576).toFixed(1)}MB)`);
    } catch(e) {
        // Non-fatal: this only persists the tar for next session. The CURRENT
        // session serves from the in-memory buffer/index, so a failed write
        // here does NOT cause "no images" — say so, because this warning was
        // the only line in a 0.2.8 "images not loading" report and read like
        // the cause when it isn't. The real diagnostic is IMAGE_RESOLVE_FAIL.
        console.warn('[ImageLoader] Cache write failed (non-fatal, serving from memory; not the cause of missing images):', e.message);
    }
}

export async function loadImageTar(onProgress) {
    if (_tarLoaded) return;
    if (_tarLoading) return _tarLoading;
    // A background/manual heal is the same download — join it instead of
    // racing a second full-body fetch (game-entry gate vs retry timer).
    if (_tarHealInFlight) return _tarHealInFlight;

    _tarLoading = _doLoadTar(onProgress);
    await _tarLoading;
    _tarLoading = null;
}

/**
 * Background load of the REST tar (heavy per-location / event art). Best-effort
 * and never awaited by the boot gate: a failure leaves the heavy art missing
 * but the game playable on the core set, instead of a 170MB single download
 * gating the whole session (split-tar, 0.2.8.1.2). No Cache-API persistence
 * (the rest is large and the cache write is exactly what choked constrained
 * Android, VPS 0.2.8.1.1) and bounded retries on a short read. A 404 means an
 * older single-tar build — no-op.
 */
export async function loadRestTar() {
    if (_restLoaded) return;
    if (_restLoading) return _restLoading;
    _restLoading = _doLoadRest();
    try { await _restLoading; } finally { _restLoading = null; }
}

async function _doLoadRest() {
    for (let attempt = 1; attempt <= 3; attempt++) {
        let resp;
        try {
            resp = await fetchWithEdgeRetry(REST_TAR_URL);
        } catch (e) {
            console.warn(`[ImageLoader] rest tar fetch failed (attempt ${attempt}/3): ${e?.message || e}`);
            await new Promise((r) => setTimeout(r, attempt * 4000));
            continue;
        }
        if (resp.status === 404) {
            console.log('[ImageLoader] No rest tar (single-tar build) — core only');
            _restLoaded = true;
            try { resp.body && resp.body.cancel(); } catch (e) { /* ignore */ }
            return;
        }
        const total = parseInt(resp.headers.get('content-length') || '0', 10);
        try {
            const reader = resp.body.getReader();
            let chunks = [];
            let received = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
            }
            if (total > 0 && received < total) {
                console.warn(`[ImageLoader] rest tar truncated (${received}/${total}), attempt ${attempt}/3`);
                chunks = null;
                await new Promise((r) => setTimeout(r, attempt * 4000));
                continue;
            }
            let buf = new Uint8Array(received);
            let off = 0;
            for (const c of chunks) { buf.set(c, off); off += c.length; }
            // Release the ~122MB of stream fragments BEFORE the _buildIndex
            // transfer-copy and the Blob copy allocate. Holding chunks + buf +
            // index-slice + Blob simultaneously is the OOM mechanism that left
            // the heavy REST tar (~2/3 of all art) unloadable on constrained
            // Android tablets — the smaller CORE tar survived the same pattern.
            // (Android image fix, foundby:Sam 0.2.8.4.1.)
            chunks = null;
            const index = await _buildIndex(buf);
            // Swap blob + index together so getImageUrl never sees a half-built
            // rest index (same atomic-swap rule as the core).
            _restBlob = new Blob([buf], { type: 'application/x-tar' });
            _restIndex = index;
            // _extractLazy serves rest images from _restBlob (a disk-backable
            // Blob), never from buf — drop the contiguous 122MB heap copy now.
            buf = null;
            _restLoaded = true;
            _restLoadFailed = false;
            _restRetryCount = 0;
            if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
            console.log(`[ImageLoader] Rest tar indexed ${Object.keys(index).length} images (${(received / 1048576).toFixed(1)}MB)`);
            return;
        } catch (e) {
            console.warn(`[ImageLoader] rest tar load failed (attempt ${attempt}/3): ${e?.message || e}`);
            await new Promise((r) => setTimeout(r, attempt * 4000));
        }
    }
    // Inline budget exhausted — schedule a bounded background self-heal so a
    // transient OOM / network / CDN-edge failure recovers mid-session instead
    // of leaving the heavy art dead until the app restarts (mirrors the core
    // tar's _scheduleTarRetry; the 'online' event resets the budget).
    _restLoadFailed = true;
    console.warn('[ImageLoader] rest tar did not load after 3 attempts — scheduling background retry (heavy art unavailable until it lands; core images intact)');
    _scheduleRestRetry();
}

async function _doLoadTar(onProgress) {
    _lazyMode = true;
    _LRU_MAX = _isMobile() ? _LRU_MAX_MOBILE : _LRU_MAX_DESKTOP;

    // Step 1: Start the real GET — we'll pull content-length off its headers.
    // Previously did a separate HEAD for size + cache-validation, then a GET.
    // That served a pre-probeImageTar era; with probeImageTar already confirming
    // the file exists, doing another HEAD here was a) redundant and b) the
    // source of a Firefox stall: python3 -m http.server serializes connections,
    // so HEAD → GET on Firefox's HTTP/1.1 reuse path occasionally deadlocked
    // on the GET body (never yielded chunks). One GET, no prior HEAD, avoids it.
    console.log('[ImageLoader] Downloading images.tar ...');
    // Edge-403-resilient: itch.io's BunnyCDN edge intermittently 403s a bare
    // GET that succeeds on retry (same class as story-fetch.js). Pre-fix a
    // single transient 403 here dropped the player into loose-image mode for
    // the whole session — every non-location image blank. Retries force
    // cache:'reload' so a negatively-cached edge response isn't replayed.
    let resp;
    try {
        resp = await fetchWithEdgeRetry(TAR_CACHE_URL);
    } catch (e) {
        console.error('[ImageLoader] images.tar load failed:', e?.message || e);
        _scheduleTarRetry();
        _emitTarState(false);
        return;
    }
    const tarSize = parseInt(resp.headers.get('content-length') || '0', 10);

    // Step 2: Try IndexedDB cache (skip the 90MB body read on cache hit).
    // If hit, we abort the response stream — we already have the bytes.
    if (tarSize > 0 && !_tarComplete) {
        const cacheHit = await _tryLoadFromCache(tarSize);
        if (cacheHit) {
            try { resp.body.cancel(); } catch(e) { /* ignore */ }
            if (onProgress) onProgress(1);
            return;
        }
    }

    // Step 3: Body download with truncation guard. A flaky CDN edge or
    // cellular link can close the stream early WITHOUT raising an error —
    // pre-fix the truncated buffer was indexed silently and the session ran
    // with partial (or near-zero) images and not a single log line (VPS
    // report 2026-06-09 "No pic loading", Android). Detect the short read
    // and re-pull before settling for the partial archive. The re-pull is a
    // Range request resuming at the bytes we already have — a 95% truncation
    // then costs ~the missing tail, not another full download.
    let buffer = null;
    for (let bodyAttempt = 1; bodyAttempt <= 2; bodyAttempt++) {
        try {
            // Range-resume validation: only append when the server resumed at
            // EXACTLY our offset for the SAME total size — anything else
            // (file changed mid-session, proxy ignored the Range semantics)
            // would corrupt the concat, so fall back to keeping the partial.
            let appendBase = null;
            if (resp.status === 206) {
                const cr = resp.headers.get('content-range') || '';
                const m = cr.match(/bytes\s+(\d+)-\d+\/(\d+)/i);
                const start = m ? parseInt(m[1], 10) : -1;
                const total = m ? parseInt(m[2], 10) : -1;
                if (buffer && start === buffer.length && (!tarSize || total === tarSize)) {
                    appendBase = buffer;
                } else {
                    console.warn(`[ImageLoader] Range resume rejected (${cr || 'no content-range'})`);
                    try { resp.body.cancel(); } catch(e2) { /* ignore */ }
                    break; // keep the partial we have
                }
            }
            const respLen = parseInt(resp.headers.get('content-length') || '0', 10);
            const expectedTotal = tarSize || (appendBase ? 0 : respLen);

            const reader = resp.body.getReader();
            const chunks = [];
            let received = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                if (onProgress && expectedTotal > 0) {
                    onProgress(((appendBase ? appendBase.length : 0) + received) / expectedTotal);
                }
            }

            // Concatenate chunks (appended onto the resumed prefix when 206)
            const baseLen = appendBase ? appendBase.length : 0;
            const buf = new Uint8Array(baseLen + received);
            if (appendBase) buf.set(appendBase, 0);
            let offset = baseLen;
            for (const chunk of chunks) {
                buf.set(chunk, offset);
                offset += chunk.length;
            }
            // Keep the LONGEST attempt — a flaky retry must never downgrade
            // a longer partial to a shorter one.
            if (!buffer || buf.length > buffer.length) buffer = buf;
            if (!expectedTotal || buf.length >= expectedTotal) {
                buffer = buf;
                if (bodyAttempt > 1) {
                    // warn-level on purpose: recovery breadcrumbs must survive
                    // the production console.log suppression (main.js) so they
                    // show up in bug-report console captures.
                    console.warn('[ImageLoader] Recovered full images.tar on body re-pull');
                }
                break; // complete
            }
            console.warn(`[ImageLoader] images.tar truncated (${buf.length}/${expectedTotal} bytes), attempt ${bodyAttempt}/2`);
        } catch (e) {
            // Mid-stream network error. Pre-fix this rejected _tarLoading and
            // propagated into the boot sequence — now we retry/degrade instead.
            console.warn(`[ImageLoader] images.tar body read failed (attempt ${bodyAttempt}/2): ${e?.message || e}`);
        }
        if (bodyAttempt === 1) {
            try {
                if (buffer && buffer.length > 0 && tarSize > 0) {
                    resp = await fetch(TAR_CACHE_URL, {
                        cache: 'reload',
                        headers: { 'Range': `bytes=${buffer.length}-` },
                    });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                } else {
                    resp = await fetchWithEdgeRetry(TAR_CACHE_URL);
                }
            } catch (e) {
                break; // keep whatever partial data we have
            }
        }
    }

    if (!buffer || buffer.length === 0) {
        console.error('[ImageLoader] images.tar download produced no data — background retry scheduled');
        _scheduleTarRetry();
        _emitTarState(false);
        return;
    }

    // Background retry must never replace a complete archive with a partial
    // one, nor an installed partial with an equal-or-shorter one.
    const complete = !(tarSize > 0 && buffer.length < tarSize);
    if (_tarComplete && !complete) return;
    if (!complete && _tarBlob && buffer.length <= _tarBlob.size) {
        console.warn(`[ImageLoader] Retry produced a shorter partial (${buffer.length} <= ${_tarBlob.size} bytes) — keeping current index`);
        _scheduleTarRetry();
        return;
    }

    // Build into a local index, then swap blob+index together (atomic from
    // the perspective of getImageUrl callers). Pre-fix the background heal
    // set `_tarIndex = {}` and re-parsed for 0.5-2s — every image rendered
    // in that window silently resolved to null.
    const index = await _buildIndex(buffer);
    _tarBlob = new Blob([buffer], { type: 'application/x-tar' });
    _tarIndex = index;
    _tarLoaded = true;
    _tarComplete = complete;
    console.log(`[ImageLoader] Indexed ${Object.keys(_tarIndex).length} images (${(buffer.length / 1048576).toFixed(1)}MB)`);

    if (!complete) {
        // Partial archive beats none for THIS session (images up to the cut
        // resolve; _extractLazy bounds-guards the rest), but don't cache it
        // and keep trying for the full file in the background.
        console.warn(`[ImageLoader] Running on truncated tar (${buffer.length}/${tarSize} bytes) — background retry scheduled`);
        _scheduleTarRetry();
        _emitTarState(false);
        return;
    }

    // Step 4: Save to IndexedDB cache (async, non-blocking)
    _saveTarToCache(buffer, buffer.length);
    _emitTarState(true);
}

/**
 * Schedule a background re-download after a failed or truncated tar load,
 * so a session that booted image-less self-heals once the network/CDN edge
 * recovers. Bounded attempts with growing backoff; no-op once complete.
 */
function _scheduleTarRetry() {
    if (typeof window === 'undefined') return;
    if (_tarComplete || _tarRetryTimer || _tarRetryCount >= _TAR_RETRY_DELAYS.length) return;
    const delay = _TAR_RETRY_DELAYS[_tarRetryCount++];
    // warn-level on purpose — must survive production console.log suppression
    // so the breadcrumb lands in bug-report console captures.
    console.warn(`[ImageLoader] Tar background retry ${_tarRetryCount}/${_TAR_RETRY_DELAYS.length} in ${Math.round(delay / 1000)}s`);
    _tarRetryTimer = setTimeout(() => {
        _tarRetryTimer = null;
        _runTarHeal('Background tar retry');
    }, delay);
}

/**
 * Background self-heal for the REST tar — bounded retries with growing backoff
 * so a session whose heavy art (~2/3 of images) failed to stream/index recovers
 * without an app restart. Mirrors _scheduleTarRetry and reuses _TAR_RETRY_DELAYS;
 * the window 'online' listener resets _restRetryCount for a fresh budget.
 */
function _scheduleRestRetry() {
    if (typeof window === 'undefined') return;
    if (_restLoaded || _restRetryTimer || _restRetryCount >= _TAR_RETRY_DELAYS.length) return;
    const delay = _TAR_RETRY_DELAYS[_restRetryCount++];
    // warn-level on purpose — survives production console.log suppression so the
    // breadcrumb lands in bug-report console captures.
    console.warn(`[ImageLoader] Rest-tar background retry ${_restRetryCount}/${_TAR_RETRY_DELAYS.length} in ${Math.round(delay / 1000)}s`);
    _restRetryTimer = setTimeout(() => {
        _restRetryTimer = null;
        loadRestTar().catch((e) => console.warn('[ImageLoader] Rest-tar retry failed:', e?.message || e));
    }, delay);
}

// Single in-flight guard shared by the retry timer, the window 'online'
// listener and the manual retry button — a heal is never run twice
// concurrently, callers just join the running promise.
let _tarHealInFlight = null;

function _runTarHeal(label) {
    if (_tarComplete) return Promise.resolve();
    if (_tarHealInFlight) return _tarHealInFlight;
    // The boot download is still in flight (parallelized boot) — join it;
    // if it fails it schedules its own retry.
    if (_tarLoading) return _tarLoading;
    _tarHealInFlight = _doLoadTar(null).then(() => {
        if (_tarComplete) {
            console.warn(`[ImageLoader] ${label} succeeded — images restored`);
        }
    }).catch((e) => {
        console.warn(`[ImageLoader] ${label} failed:`, e?.message || e);
        _scheduleTarRetry();
    }).finally(() => {
        _tarHealInFlight = null;
    });
    return _tarHealInFlight;
}

/**
 * Player-triggered retry (the "images failed" banner button). Resets the
 * bounded retry budget — an explicit click is a fresh signal that the
 * network may be back.
 */
export function retryTarNow() {
    // A dead-backing core recovery may already be in flight (auto-heal) — join it.
    if (_tarDeadHealing) return _tarHealInFlight || Promise.resolve();
    // Manual tap also re-fetches the heavy REST tar (owner choice: rest is
    // manual-only, this button IS the manual path). Fires even when core is fine.
    if (!_restLoaded) {
        if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
        _restRetryCount = 0;
        loadRestTar().catch(() => {});
    }
    if (_tarComplete) return Promise.resolve();
    if (_tarRetryTimer) { clearTimeout(_tarRetryTimer); _tarRetryTimer = null; }
    _tarRetryCount = 0;
    return _runTarHeal('Manual tar retry');
}

/**
 * Notify UI listeners about tar state transitions. `pno-tar-state` drives
 * the failure banner (detail = getTarStatus()); `pno-tar-healed` lets
 * story-content re-resolve placeholder cards in the active scene.
 */
function _emitTarState(healed) {
    if (typeof window === 'undefined') return;
    try {
        window.dispatchEvent(new CustomEvent('pno-tar-state', { detail: getTarStatus() }));
        if (healed) window.dispatchEvent(new CustomEvent('pno-tar-healed'));
    } catch (e) { /* non-fatal */ }
}

/**
 * Build tar index without creating Blob URLs.
 * Uses Web Worker to avoid blocking the main thread during tar parsing.
 * Falls back to synchronous parsing if workers unavailable.
 */
const _TAR_WORKER_CODE = `
self.onmessage = function(e) {
    const buffer = new Uint8Array(e.data);
    const index = {};
    let pos = 0;
    const decoder = new TextDecoder();
    const mimeMap = { webp: 'image/webp', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };
    while (pos + 512 <= buffer.length) {
        const header = buffer.slice(pos, pos + 512);
        if (header.every(b => b === 0)) break;
        let nameEnd = 0;
        while (nameEnd < 100 && header[nameEnd] !== 0) nameEnd++;
        const fullName = decoder.decode(header.slice(0, nameEnd));
        let sizeStr = '';
        for (let i = 124; i < 136; i++) {
            const ch = header[i];
            if (ch === 0 || ch === 32) break;
            sizeStr += String.fromCharCode(ch);
        }
        const fileSize = parseInt(sizeStr, 8) || 0;
        const typeFlag = header[156];
        pos += 512;
        if ((typeFlag === 0 || typeFlag === 48) && fileSize > 0) {
            const ext = fullName.split('.').pop().toLowerCase();
            const mime = mimeMap[ext];
            if (mime) {
                const parts = fullName.split('/');
                const fileName = parts[parts.length - 1];
                const baseName = fileName.replace(/\\.(webp|png|jpg|jpeg)$/i, '');
                index[baseName] = { offset: pos, size: fileSize, mime: mime };
            }
        }
        pos += Math.ceil(fileSize / 512) * 512;
    }
    self.postMessage(index);
};
`;

function _buildIndex(buffer) {
    // Resolves with the parsed index — callers swap _tarBuffer/_tarIndex
    // together so lookups never run against a half-built index.
    return new Promise((resolve) => {
        // Try Web Worker for off-main-thread parsing
        try {
            const blob = new Blob([_TAR_WORKER_CODE], { type: 'application/javascript' });
            const workerUrl = URL.createObjectURL(blob);
            const worker = new Worker(workerUrl);
            worker.onmessage = (e) => {
                worker.terminate();
                URL.revokeObjectURL(workerUrl);
                resolve(e.data);
            };
            worker.onerror = () => {
                // Fallback to synchronous
                worker.terminate();
                URL.revokeObjectURL(workerUrl);
                resolve(_buildIndexSync(buffer));
            };
            // Copy the buffer (the main thread keeps the original for
            // _extractLazy), then TRANSFER the copy — without the transfer
            // list postMessage structured-clones the full archive a second
            // time on every tar-mode boot, including Cache-API hits.
            const ab = buffer.buffer.slice(0);
            worker.postMessage(ab, [ab]);
        } catch(e) {
            // Fallback to synchronous if workers unavailable
            resolve(_buildIndexSync(buffer));
        }
    });
}

/** Synchronous fallback for _buildIndex (used when Web Worker unavailable) */
function _buildIndexSync(buffer) {
    const index = {};
    let pos = 0;
    const decoder = new TextDecoder();
    const mimeMap = { webp: 'image/webp', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };

    while (pos + 512 <= buffer.length) {
        const header = buffer.slice(pos, pos + 512);
        if (header.every(b => b === 0)) break;

        let nameEnd = 0;
        while (nameEnd < 100 && header[nameEnd] !== 0) nameEnd++;
        const fullName = decoder.decode(header.slice(0, nameEnd));

        let sizeStr = '';
        for (let i = 124; i < 136; i++) {
            const ch = header[i];
            if (ch === 0 || ch === 32) break;
            sizeStr += String.fromCharCode(ch);
        }
        const fileSize = parseInt(sizeStr, 8) || 0;
        const typeFlag = header[156];

        pos += 512;

        if ((typeFlag === 0 || typeFlag === 48) && fileSize > 0) {
            const ext = fullName.split('.').pop().toLowerCase();
            const mime = mimeMap[ext];
            if (mime) {
                const parts = fullName.split('/');
                const fileName = parts[parts.length - 1];
                const baseName = fileName.replace(/\.(webp|png|jpg|jpeg)$/i, '');
                index[baseName] = { offset: pos, size: fileSize, mime };
            }
        }

        pos += Math.ceil(fileSize / 512) * 512;
    }
    return index;
}

/**
 * Extract a single image from the tar buffer (lazy mode).
 * Creates Blob URL and caches it with LRU eviction.
 */
function _extractLazy(name) {
    // Split-tar resolve: core (_tarBlob/_tarIndex) first, then the background
    // rest (_restBlob/_restIndex). Basenames are unique across both tars.
    let entry = _tarIndex && _tarIndex[name];
    let srcBlob = _tarBlob;
    if (!entry && _restIndex && _restIndex[name]) {
        entry = _restIndex[name];
        srcBlob = _restBlob;
    }
    if (!entry || !srcBlob) return null;
    // Truncated-tar guard: an entry indexed past the end of a short archive
    // would slice garbage and hand the <img> a corrupt blob. Return null so
    // the caller's miss path (placeholder / hide) handles it; the background
    // retry replaces the archive with the full one when it lands.
    if (entry.offset + entry.size > srcBlob.size) return null;

    // Zero-copy: Blob.slice is a view into the parent Blob, no byte copy.
    const blob = srcBlob.slice(entry.offset, entry.offset + entry.size, entry.mime);
    const blobUrl = URL.createObjectURL(blob);

    _imageBlobs[name] = blobUrl;
    _allBlobUrls.add(blobUrl);
    _lruQueue.push(name);

    // Evict oldest from cache. NEVER revoke a URL still referenced by a
    // live DOM node — the scroll-append story panel keeps old scene cards
    // with their `src=blob:` (foundby:Jürgen v0.2.5.7.1 "viele Bilder
    // werden nicht angezeigt"). But un-revoked object URLs are PINNED in
    // the document's blob URL store — GC can never reclaim them (the
    // pre-fix comment's "browser GC will free the blob" assumption was
    // wrong, ~1-3MB leak per distinct image). Marathon sessions exhausted
    // blob storage until images stopped loading (VPS report 2026-06-09
    // "Images stopped", Day 31). Revoke immediately when unreferenced,
    // defer to the panel-clear sweep otherwise.
    while (_lruQueue.length > _LRU_MAX) {
        const evict = _lruQueue.shift();
        const url = _imageBlobs[evict];
        delete _imageBlobs[evict];
        if (url) _revokeWhenUnreferenced(url);
    }

    return blobUrl;
}

/**
 * True when a blob URL is still referenced by a live DOM node — either an
 * <img>/<video> src or an inline-style background (location map tiles set
 * style.backgroundImage = url(blob:...), locations.js).
 */
function _isUrlReferenced(url) {
    try {
        return !!(document.querySelector(`[src="${url}"]`) ||
                  document.querySelector(`[style*="${url}"]`));
    } catch (e) {
        return true; // can't tell — keep it alive
    }
}

function _revokeWhenUnreferenced(url) {
    if (_pinnedUrls.has(url)) return;
    if (_isUrlReferenced(url)) {
        _pendingRevoke.add(url);
        return;
    }
    try { URL.revokeObjectURL(url); } catch (e) { /* already revoked */ }
    _allBlobUrls.delete(url);
    _pendingRevoke.delete(url);
}

/**
 * Re-check deferred revokes. Called from story-content's panel clear —
 * the moment scrolled-out cards (and their blob src references) leave the
 * DOM. Worst case (still referenced, e.g. open gallery) the URL just stays
 * in the deferred set for the next sweep, identical to pre-fix behaviour.
 */
export function sweepEvictedBlobs() {
    for (const url of Array.from(_pendingRevoke)) {
        _revokeWhenUnreferenced(url);
    }
}

/**
 * E-05 v0.2.5: move `name` to the end of the LRU queue (true-LRU on cache hit).
 * Without this, _lruQueue is FIFO not LRU — an image accessed every frame still
 * gets evicted in order of FIRST extraction, not last access. The cap was always
 * in place (200 desktop / 60 mobile via _LRU_MAX), but recently-accessed images
 * could get culled while truly-cold ones stayed.
 */
function _touchLRU(name) {
    const idx = _lruQueue.indexOf(name);
    if (idx >= 0) {
        _lruQueue.splice(idx, 1);
        _lruQueue.push(name);
    }
}

/**
 * Get a Blob URL for an image by name (without extension).
 * In lazy mode, extracts on-demand from the tar buffer.
 * Returns null if not found.
 */
export function getImageUrl(name) {
    // Check cache first — update LRU position on hit so frequently-used
    // images don't get evicted by a one-shot scan of unrelated images.
    if (_imageBlobs[name]) {
        _touchLRU(name);
        return _imageBlobs[name];
    }
    // Demand-triggered rest-tar heal: the player reached a scene whose art lives
    // in the rest tar, but the rest tar already spent its bounded retry budget
    // this session. On a degraded-but-online mobile link the 'online' event
    // never fires, so without this the heavy art stays blank until app restart.
    // Reset the budget once and re-stream; _restLoadFailed flips back to true on
    // the next exhaustion, so a later scene can trigger a fresh attempt. Gated on
    // _restLoadFailed first (cheap, usually false) to keep this hot path cheap;
    // loadRestTar's _restLoading guard de-dupes concurrent kicks.
    if (_restLoadFailed && !_restBlob && _restIndex && _restIndex[name]) {
        _restLoadFailed = false;
        _restRetryCount = 0;
        if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
        console.warn('[ImageLoader] Rest image requested after exhaustion — re-streaming images-rest.tar');
        loadRestTar().catch(() => {});
    }
    // Lazy extraction — core tar first, then the background rest tar.
    if (_lazyMode && ((_tarIndex && _tarIndex[name]) || (_restIndex && _restIndex[name]))) {
        return _extractLazy(name);
    }
    return null;
}

// ─── Image compatibility mode (broken device storage) ──────────────────────
// Blob URLs point into the browser's blob store, which is disk-backed for
// big tar slices; on devices with a broken/full profile storage the <img>
// fetch of a blob: URL fails even though the tar bytes are readable
// (Android reports 2026-07-11: Cache.put "Unexpected internal error" +
// MEDIA_RENDER_FAIL scheme=blob). data: URIs are plain strings — no storage
// layer involved. Compat mode renders every scene image via data: URI.
// Opt-in via the render-fail banner (story-content) — persisted so it
// survives reloads on the affected device.
const IMG_COMPAT_KEY = 'PnO_ImgCompatMode';
let _imgCompatMode = false;
try { _imgCompatMode = localStorage.getItem(IMG_COMPAT_KEY) === 'true'; } catch (e) { /* private mode */ }

export function isImgCompatMode() { return _imgCompatMode; }
export function setImgCompatMode(on) {
    _imgCompatMode = !!on;
    try { localStorage.setItem(IMG_COMPAT_KEY, _imgCompatMode ? 'true' : 'false'); } catch (e) { /* private mode */ }
}

const _dataUriCache = new Map();        // name -> data: URI (hot scenes only)
const _DATA_URI_CACHE_MAX = 20;

/**
 * Render-fallback: the image bytes as a data: URI (RAM-only, no blob store).
 * Async (arrayBuffer read). Returns null when the name isn't in a tar index
 * or the slice read itself fails — then the device storage is beyond
 * client-side help and the MEDIA_RENDER_FAIL diagnostic reports that too.
 */
export async function getImageDataUri(name) {
    if (_dataUriCache.has(name)) return _dataUriCache.get(name);
    let entry = _tarIndex && _tarIndex[name];
    let srcBlob = _tarBlob;
    if (!entry && _restIndex && _restIndex[name]) {
        entry = _restIndex[name];
        srcBlob = _restBlob;
    }
    if (!entry || !srcBlob) return null;
    if (entry.offset + entry.size > srcBlob.size) return null;
    const fromRest = srcBlob === _restBlob;
    try {
        const buf = await srcBlob.slice(entry.offset, entry.offset + entry.size).arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = '';
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
            bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        const uri = `data:${entry.mime};base64,${btoa(bin)}`;
        _dataUriCache.set(name, uri);
        if (_dataUriCache.size > _DATA_URI_CACHE_MAX) {
            _dataUriCache.delete(_dataUriCache.keys().next().value);
        }
        return uri;
    } catch (e) {
        console.warn('[ImageLoader] data-URI fallback read failed (device storage unreadable):', e?.message);
        // A NotReadableError on a COMPLETE tar = the Blob's disk backing died.
        // Re-fetch into a fresh Blob (core auto, rest manual). Debounced so the
        // per-image failure storm triggers one recovery, not one per image.
        if (_isDeadBackingError(e)) {
            if (fromRest) _markRestBackingDead('data-URI read');
            else _recoverDeadCoreTar('data-URI read');
        }
        return null;
    }
}

/**
 * Resolve an image path. In tar mode, returns blob URL.
 * In normal mode, returns the file path unchanged. Empty-string folder
 * means the image lives at the top level of assets/images/ (e.g. the
 * velvet_client_* and velvet_work_* brothel minigame assets) — in that
 * case we skip the folder segment so we don't produce a double-slash
 * URL (which works in browsers but is ugly in DevTools).
 */
// One-shot image-subsystem health breadcrumb. The old "no images" reports
// carried only the non-fatal cache-write warning — nothing about WHY images
// failed. This fires (once/session, warn-level so it survives production
// console.log suppression and lands in VPS bug-report console captures) the
// first time an image can't be resolved, dumping the state that actually
// distinguishes the failure modes:
//   tarLoaded=false indexed=0   -> archive never loaded (download/CDN/storage)
//   tarLoaded=true  indexed=N   -> archive fine, single name missing from index
//   tarAvailable=false          -> loose mode (HEAD probe failed) — scene imgs 404 on Free
let _imageHealthLogged = false;
function _logImageHealth(reason, name) {
    if (_imageHealthLogged) return;
    _imageHealthLogged = true;
    console.warn(`[ImageLoader] IMAGE_RESOLVE_FAIL: ${reason} | name=${name}`
        + ` tarAvailable=${_tarAvailable} tarLoaded=${_tarLoaded} tarComplete=${_tarComplete}`
        + ` lazyMode=${_lazyMode} indexed=${_tarIndex ? Object.keys(_tarIndex).length : 0}`
        + ` blobs=${Object.keys(_imageBlobs).length} retries=${_tarRetryCount}`
        + ` cacheAPI=${_hasCacheAPI()} mobile=${_isMobile()}`);
}

export function resolveImage(folder, name) {
    if (_tarAvailable && _tarLoaded) {
        const url = getImageUrl(name);
        // Archive loaded but this name isn't in the index — a genuinely missing
        // asset, not a systemic failure (indexed=N in the breadcrumb makes that clear).
        if (!url) _logImageHealth('tar loaded but name not in index', name);
        return url || null;
    }
    // tar mode was chosen but the archive isn't ready: the loose path below
    // 404s for scene images that don't ship loose (Free/itch). This is the
    // systemic "no images" case — surface it instead of failing silently.
    if (_tarAvailable && !_tarLoaded) {
        _logImageHealth('tar mode active but archive not loaded (download/CDN/storage failure)', name);
    }
    return folder ? `assets/images/${folder}/${name}.png` : `assets/images/${name}.png`;
}

/**
 * Revoke all blob URLs to free memory.
 */
export function revokeAllBlobUrls() {
    let count = 0;
    // Revoke EVERY URL we ever created — including ones evicted from
    // the LRU cache (those were left alive on purpose so DOM <img>
    // elements don't break mid-session; pagehide is the right time
    // to actually clean them up).
    for (const url of _allBlobUrls) {
        URL.revokeObjectURL(url);
        count++;
    }
    _allBlobUrls.clear();
    for (const name in _imageBlobs) delete _imageBlobs[name];
    _lruQueue.length = 0;
    // In lazy mode, also release both tar blobs (core + background rest).
    if (_lazyMode) {
        _tarBlob = null;
        _tarIndex = null;
        _restBlob = null;
        _restIndex = null;
        _restLoaded = false;
    }
    console.log(`[ImageLoader] Revoked ${count} blob URLs`);
}

// Auto-revoke on page unload. bfcache (persisted=true) means the page may
// come back alive via back/forward — revoking then would strand the restore
// with dead blob URLs while _tarLoaded=true blocks every recovery path
// (loadImageTar guard + retry guard both early-return).
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', (e) => {
        if (e.persisted) return;
        revokeAllBlobUrls();
    });
    window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return; // normal navigation — boot path handles it
        // Restored from bfcache. If teardown ran anyway (some UAs fire
        // pagehide without persisted, then restore), rebuild — near-instant
        // via the Cache API copy.
        if (_tarAvailable && (!_tarIndex || !_tarBlob)) {
            _tarLoaded = false;
            _tarComplete = false;
            _tarRetryCount = 0;
            console.warn('[ImageLoader] bfcache restore with torn-down tar state — rebuilding');
            loadImageTar().then(() => loadRestTar().catch(() => {})).catch(() => {});
        } else if (!_restBlob || !_restIndex) {
            // Core survived but the background rest was torn down — re-stream it
            // with a fresh retry budget.
            _restLoaded = false;
            _restRetryCount = 0;
            if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
            loadRestTar().catch(() => {});
        }
    });
    // Connectivity returned: the bounded retry budget (3 attempts / ~3 min)
    // can exhaust during a longer offline gap right before the network comes
    // back — the OS signal is a fresh budget.
    window.addEventListener('online', () => {
        if (!_tarAvailable) return;
        // Core tar heal (only if it never completed).
        if (!_tarComplete) {
            console.warn('[ImageLoader] Connection restored — retrying images.tar');
            if (_tarRetryTimer) { clearTimeout(_tarRetryTimer); _tarRetryTimer = null; }
            _tarRetryCount = 0;
            _runTarHeal('Online-event tar retry');
        }
        // Rest tar heal (if the heavy art never finished streaming). Fresh
        // budget on reconnect — the bounded inline + scheduled retries can have
        // exhausted during a longer offline gap right before the network came
        // back. Independent of core: core usually completes, so this must run
        // even when _tarComplete is true.
        if (!_restLoaded) {
            console.warn('[ImageLoader] Connection restored — retrying images-rest.tar');
            if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
            _restRetryCount = 0;
            loadRestTar().catch(() => {});
        }
    });
    // App returned to the foreground (Android task-switch, screen unlock). On a
    // weak-but-online mobile link the 'online' event never fires, so a foreground
    // signal is the other natural moment to reset an exhausted rest budget. Gate
    // on _restLoadFailed so we never interrupt a still-scheduled retry — that
    // flag is only set true after the bounded budget is fully spent.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (!_tarAvailable) return;
        if (!_restLoaded && _restLoadFailed) {
            console.warn('[ImageLoader] Foreground — retrying images-rest.tar');
            if (_restRetryTimer) { clearTimeout(_restRetryTimer); _restRetryTimer = null; }
            _restLoadFailed = false;
            _restRetryCount = 0;
            loadRestTar().catch(() => {});
        }
    });
}

/** Diagnostic snapshot for the analytics session payload (assets block). */
export function getTarStatus() {
    return {
        mode: _tarAvailable ? 'tar' : 'loose',
        loaded: _tarLoaded,
        complete: _tarComplete,
        retries: _tarRetryCount,
        indexed: _tarIndex ? Object.keys(_tarIndex).length : 0,
        // Rest (heavy) tar — distinguishes "core OK, rest dead" (the Android
        // 2/3-images-missing class) from a full failure in analytics captures.
        restLoaded: _restLoaded,
        restIndexed: _restIndex ? Object.keys(_restIndex).length : 0,
        restRetries: _restRetryCount,
        restFailed: _restLoadFailed,
        // Broken-device-storage class (2026-07-11): compat-mode state + the
        // render-fail/retry counters story-content maintains, so bug reports
        // show whether the data-URI fallback actually worked on that device.
        compatMode: _imgCompatMode,
        renderStats: (typeof window !== 'undefined' && window.__pnoRenderStats) || null,
    };
}
if (typeof window !== 'undefined') {
    // analytics.js reads via window — house style for diagnostics that would
    // otherwise create import cycles (see __bracketBugs / __stateViolations).
    window.__tarStatus = getTarStatus;
    // Read-only diagnostic accessor for the data-URI fallback path. Used by the
    // dead-backing recovery regression test to drive getImageDataUri for a
    // known-present tar entry (real scene names aren't in a synthetic test tar).
    window.__imgDataUri = getImageDataUri;
}
